import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  color: white;
`;

const HeroSection = styled.div`
  text-align: center;
  padding: 40px 20px 60px;
  
  @media (min-width: 768px) {
    padding: 60px 20px 80px;
  }
`;

const MainTitle = styled.h1`
  font-size: 48px;
  font-weight: 900;
  margin: 0 0 16px 0;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  
  @media (min-width: 768px) {
    font-size: 64px;
    margin: 0 0 20px 0;
  }
`;

const Tagline = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #f0f8ff;
  
  @media (min-width: 768px) {
    font-size: 32px;
    margin: 0 0 16px 0;
  }
`;

const Subtitle = styled.p`
  font-size: 18px;
  margin: 0;
  color: #e6f3ff;
  font-weight: 500;
  
  @media (min-width: 768px) {
    font-size: 20px;
  }
`;

const CTAButton = styled.button`
  background: #28a745;
  color: white;
  border: none;
  padding: 18px 32px;
  border-radius: 50px;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 30px;
  box-shadow: 0 4px 15px rgba(40, 167, 69, 0.4);
  transition: all 0.3s ease;
  
  &:hover {
    background: #218838;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(40, 167, 69, 0.6);
  }
  
  @media (min-width: 768px) {
    padding: 20px 40px;
    font-size: 20px;
    margin-top: 40px;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  gap: 40px;
  margin-top: 60px;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 60px;
    margin-top: 80px;
  }
`;

const Section = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  @media (min-width: 768px) {
    padding: 40px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (min-width: 768px) {
    font-size: 28px;
    margin: 0 0 24px 0;
  }
`;

const ProcessStep = styled.div`
  display: flex;
  align-items: center;
  margin: 16px 0;
  font-size: 16px;
  
  @media (min-width: 768px) {
    font-size: 18px;
    margin: 20px 0;
  }
`;

const Arrow = styled.span`
  margin: 0 12px;
  font-size: 20px;
  color: #ffd700;
`;

const StatBox = styled.div`
  background: rgba(255, 255, 255, 0.15);
  border-radius: 12px;
  padding: 20px;
  margin: 16px 0;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const StatNumber = styled.div`
  font-size: 32px;
  font-weight: 900;
  color: #ffd700;
  margin-bottom: 8px;
  
  @media (min-width: 768px) {
    font-size: 40px;
  }
`;

const StatLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  
  @media (min-width: 768px) {
    font-size: 18px;
  }
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 20px 0;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: center;
  margin: 12px 0;
  font-size: 16px;
  
  &:before {
    content: "✅";
    margin-right: 12px;
    font-size: 18px;
  }
  
  @media (min-width: 768px) {
    font-size: 18px;
    margin: 16px 0;
  }
`;

const FormulaSection = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 40px 30px;
  margin-top: 40px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  
  @media (min-width: 768px) {
    padding: 50px 40px;
  }
`;

const FormulaText = styled.div`
  font-size: 20px;
  font-weight: 700;
  margin: 20px 0;
  
  @media (min-width: 768px) {
    font-size: 24px;
    margin: 30px 0;
  }
`;

const PriceTag = styled.div`
  font-size: 48px;
  font-weight: 900;
  color: #ffd700;
  margin: 20px 0;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  
  @media (min-width: 768px) {
    font-size: 56px;
    margin: 30px 0;
  }
`;

const ShowingBookerLanding = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    // Check if user is logged in by looking for a token
    const token = localStorage.getItem('userToken');
    if (token) {
      navigate('/showingbooker/dashboard');
    } else {
      navigate('/login');
    }
  };

  return (
    <Container>
      <HeroSection>
        <MainTitle>ShowingBooker</MainTitle>
        <Tagline>"Compliant, Automated Showing Management"</Tagline>
        <Subtitle>Professional real estate showing coordination with built-in legal compliance</Subtitle>
        <CTAButton onClick={handleGetStarted}>
          🚀 Get Started Now
        </CTAButton>
      </HeroSection>

      <ContentGrid>
        <Section>
          <SectionTitle>⚡ The Automation Advantage</SectionTitle>
          
          <div style={{ marginBottom: '30px' }}>
            <h4 style={{ color: '#ffcccb', marginBottom: '16px' }}>Traditional Showing Process: 2-3 Days</h4>
            <ProcessStep>
              Call listing agent <Arrow>➜</Arrow> Wait for callback
            </ProcessStep>
            <ProcessStep>
              Agent calls tenant <Arrow>➜</Arrow> Wait for response
            </ProcessStep>
            <ProcessStep>
              Coordinate schedules <Arrow>➜</Arrow> More waiting
            </ProcessStep>
            <ProcessStep>
              Maybe get confirmed <Arrow>➜</Arrow> Maybe not
            </ProcessStep>
          </div>

          <div>
            <h4 style={{ color: '#90EE90', marginBottom: '16px' }}>ShowingBooker: 2 Minutes</h4>
            <ProcessStep>
              See available times <Arrow>➜</Arrow> Book instantly
            </ProcessStep>
            <ProcessStep>
              Tenant notified <Arrow>➜</Arrow> Automatically
            </ProcessStep>
            <ProcessStep>
              Add to calendar <Arrow>➜</Arrow> Done
            </ProcessStep>
            <ProcessStep>
              Show property <Arrow>➜</Arrow> Make offer
            </ProcessStep>
          </div>
        </Section>

        <Section>
          <SectionTitle>📈 The Numbers Don't Lie</SectionTitle>
          <h4 style={{ marginBottom: '20px' }}>Homes with ShowingBooker:</h4>
          
          <StatBox>
            <StatNumber>3x</StatNumber>
            <StatLabel>more showings</StatLabel>
            <div style={{ fontSize: '14px', marginTop: '8px', opacity: '0.9' }}>
              24/7 booking availability
            </div>
          </StatBox>

          <StatBox>
            <StatNumber>50%</StatNumber>
            <StatLabel>faster sales</StatLabel>
            <div style={{ fontSize: '14px', marginTop: '8px', opacity: '0.9' }}>
              More eyes = quicker offers
            </div>
          </StatBox>

          <StatBox>
            <StatNumber>25%</StatNumber>
            <StatLabel>less time on market</StatLabel>
            <div style={{ fontSize: '14px', marginTop: '8px', opacity: '0.9' }}>
              Speed wins in real estate
            </div>
          </StatBox>
        </Section>

        <Section>
          <SectionTitle>🔒 Compliance & Security</SectionTitle>
          <BenefitsList>
            <BenefitItem>Automated tenant notice compliance</BenefitItem>
            <BenefitItem>Digital record keeping of all showings</BenefitItem>
            <BenefitItem>Secure visitor verification system</BenefitItem>
            <BenefitItem>Full audit trail of property access</BenefitItem>
          </BenefitsList>
        </Section>

        <Section>
          <SectionTitle>🏆 Professional Management</SectionTitle>
          <BenefitsList>
            <BenefitItem><strong>For Listing Agents:</strong> Automated compliance documentation</BenefitItem>
            <BenefitItem><strong>For Property Managers:</strong> Centralized showing coordination</BenefitItem>
            <BenefitItem><strong>For Owners:</strong> Secure, verified property access</BenefitItem>
          </BenefitsList>
        </Section>

        <FormulaSection>
          <SectionTitle>🎯 The ShowingBooker System</SectionTitle>
          <FormulaText>
            Automated Coordination + Legal Compliance + Access Control = 
            <span style={{ color: '#ffd700' }}> PROFESSIONAL MANAGEMENT</span>
          </FormulaText>
          
          <div style={{ 
            background: 'rgba(255, 255, 255, 0.2)', 
            borderRadius: '16px', 
            padding: '30px', 
            margin: '30px 0',
            border: '2px solid rgba(255, 255, 255, 0.3)'
          }}>
            <div style={{ fontSize: '28px', fontWeight: '900', marginBottom: '24px', color: '#90EE90', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
              💯 100% FREE for Property Owners & Agents
            </div>
            <div style={{ fontSize: '20px', marginBottom: '20px', lineHeight: '1.8' }}>
              <strong>✅ Listing Agents:</strong> Add unlimited properties - <span style={{ color: '#90EE90' }}>FREE FOREVER</span><br/>
              <strong>✅ Buyer's Agents:</strong> Send booking links to clients - <span style={{ color: '#90EE90' }}>ALWAYS FREE</span><br/>
              <strong>✅ Property Managers:</strong> Manage all showings - <span style={{ color: '#90EE90' }}>ZERO COST</span><br/>
              <strong>✅ Sellers & Landlords:</strong> List properties instantly - <span style={{ color: '#90EE90' }}>NO FEES EVER</span>
            </div>
            <div style={{ 
              fontSize: '22px', 
              fontWeight: '700', 
              background: 'rgba(144, 238, 144, 0.2)', 
              padding: '16px', 
              borderRadius: '12px',
              margin: '20px 0',
              border: '2px solid rgba(144, 238, 144, 0.5)'
            }}>
              NO setup fees • NO monthly charges • NO contracts • NO hidden costs
            </div>
            <div style={{ fontSize: '18px', fontStyle: 'italic', opacity: '0.9' }}>
              Just a simple system to improve your real estate business and life
            </div>
          </div>
          
          <div style={{ 
            background: 'rgba(255, 215, 0, 0.2)', 
            borderRadius: '16px', 
            padding: '30px', 
            margin: '30px 0',
            border: '2px solid rgba(255, 215, 0, 0.5)'
          }}>
            <PriceTag>$25</PriceTag>
            <div style={{ fontSize: '24px', fontWeight: '700', marginBottom: '16px', color: '#ffd700' }}>
              ONLY paid by the person requesting & booking the showing
            </div>
            <div style={{ fontSize: '18px', marginBottom: '20px', lineHeight: '1.6' }}>
              <strong>Who pays $25:</strong><br/>
              • Buyers booking property tours<br/>
              • Renters scheduling apartment viewings<br/>
              • Agents booking on behalf of their clients<br/>
            </div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#90EE90', marginBottom: '16px' }}>
              Who pays NOTHING:
            </div>
            <div style={{ fontSize: '18px', lineHeight: '1.6' }}>
              • Listing agents showing their properties<br/>
              • Property owners & managers<br/>
              • Anyone adding properties to the system<br/>
              • Anyone sending booking links
            </div>
          </div>
          
          <div style={{ fontSize: '20px', fontWeight: '700', marginBottom: '30px' }}>
            Professional showing management with built-in compliance and security.
          </div>
          <CTAButton onClick={handleGetStarted} style={{ marginTop: '30px', fontSize: '22px', padding: '24px 48px' }}>
            🏠 Start Adding Properties - 100% FREE
          </CTAButton>
        </FormulaSection>
      </ContentGrid>
    </Container>
  );
};

export default ShowingBookerLanding;